import { StatusBadge } from '../../../../components/base/StatusBadge';
import { formatCurrencyCompact, formatDate, formatNumber } from '../../../../utils/format';
import type { SupplyItem } from '../../../../types';

interface SupplyCardProps {
  item: SupplyItem;
  onView: (item: SupplyItem) => void;
  onEdit: (item: SupplyItem) => void;
  onInStock: (item: SupplyItem) => void;
  onOutStock: (item: SupplyItem) => void;
  onAdjust: (item: SupplyItem) => void;
  onDelete: (item: SupplyItem) => void;
}

export function SupplyCard({ item, onView, onEdit, onInStock, onOutStock, onAdjust, onDelete }: SupplyCardProps) {
  const canXuat = item.available > 0;
  const isHet = item.status === 'Hết hàng';
  const isSapHet = item.status === 'Sắp hết';

  return (
    <div className="group bg-white border-b border-slate-100 hover:bg-slate-50/60 transition-colors">
      {/* Desktop row */}
      <div className="hidden lg:grid min-w-[1000px] items-center px-4 py-3 gap-2" style={{ gridTemplateColumns: '80px minmax(0, 1fr) 96px 68px 76px 68px 88px 92px 188px' }}>
        {/* Mã */}
        <div className="text-xs font-mono text-slate-400 truncate">{item.maVatTu}</div>

        {/* Tên */}
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900 truncate">{item.name}</p>
          {item.nhaCungCap && <p className="text-xs text-slate-400 truncate">{item.nhaCungCap}</p>}
        </div>

        {/* Nhóm */}
        <div className="text-xs text-slate-600 bg-slate-100 rounded-md px-2 py-1 truncate text-center">{item.nhom}</div>

        {/* Tồn kho */}
        <div className="text-sm font-bold text-slate-900 text-right">{formatNumber(item.stock)}</div>

        {/* Khả dụng */}
        <div className="text-sm font-bold text-agrida-700 text-right">{formatNumber(item.available)}</div>

        {/* Đã cấp */}
        <div className="text-sm font-bold text-amber-700 text-right">{formatNumber(item.reserved)}</div>

        {/* Giá nhập */}
        <div className="text-sm font-medium text-slate-900 text-right">{item.giaNhap ? formatCurrencyCompact(item.giaNhap) : '—'}</div>

        {/* Trạng thái */}
        <div className="flex justify-center"><StatusBadge status={item.status} /></div>

        {/* Thao tác */}
        <div className="flex items-center justify-end gap-1">
          <button type="button" onClick={() => onView(item)} className="w-7 h-7 flex items-center justify-center rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors" title="Xem">
            <i className="ri-eye-line text-sm" />
          </button>
          <button type="button" onClick={() => onEdit(item)} className="w-7 h-7 flex items-center justify-center rounded-md text-slate-500 hover:text-agrida-700 hover:bg-agrida-50 transition-colors" title="Sửa">
            <i className="ri-edit-line text-sm" />
          </button>
          <button type="button" onClick={() => onInStock(item)} className="w-7 h-7 flex items-center justify-center rounded-md text-slate-500 hover:text-sky-700 hover:bg-sky-50 transition-colors" title="Nhập kho">
            <i className="ri-arrow-down-line text-sm" />
          </button>
          <button type="button" onClick={() => onOutStock(item)} disabled={!canXuat} className={`w-7 h-7 flex items-center justify-center rounded-md transition-colors ${canXuat ? 'text-slate-500 hover:text-amber-700 hover:bg-amber-50' : 'text-slate-300 cursor-not-allowed'}`} title="Xuất kho">
            <i className="ri-arrow-up-line text-sm" />
          </button>
          <button type="button" onClick={() => onAdjust(item)} className="w-7 h-7 flex items-center justify-center rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors" title="Điều chỉnh">
            <i className="ri-settings-3-line text-sm" />
          </button>
          <button type="button" onClick={() => onDelete(item)} className="w-7 h-7 flex items-center justify-center rounded-md text-slate-500 hover:text-red-700 hover:bg-red-50 transition-colors" title="Xóa">
            <i className="ri-delete-bin-line text-sm" />
          </button>
        </div>
      </div>

      {/* Mobile row */}
      <div className="lg:hidden px-4 py-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-slate-900 truncate">{item.name}</p>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className="text-xs font-mono text-slate-400">{item.maVatTu}</span>
              <span className="text-[11px] text-slate-600 bg-slate-100 rounded px-1.5 py-0.5">{item.nhom}</span>
            </div>
          </div>
          <StatusBadge status={item.status} />
        </div>

        <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-slate-600">
          <span>Tồn: <strong className="text-slate-900">{formatNumber(item.stock)}</strong></span>
          <span>Khả dụng: <strong className="text-agrida-700">{formatNumber(item.available)}</strong></span>
          <span>Đã cấp: <strong className="text-amber-700">{formatNumber(item.reserved)}</strong></span>
          {item.giaNhap && <span>Giá: <strong>{formatCurrencyCompact(item.giaNhap)}</strong></span>}
        </div>

        {isSapHet && (
          <div className="mt-2 flex items-center gap-1 text-amber-600 text-xs bg-amber-50 rounded-md px-2 py-1">
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-error-warning-line" /></span>
            Sắp hết: còn {formatNumber(item.stock)} {item.unit}
          </div>
        )}
        {isHet && (
          <div className="mt-2 flex items-center gap-1 text-red-600 text-xs bg-red-50 rounded-md px-2 py-1">
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-close-circle-line" /></span>
            Hết hàng
          </div>
        )}

        {(item.loHang || item.hanSuDung) && (
          <div className="mt-2 text-xs text-slate-400 flex gap-3">
            {item.loHang && <span>Lô: {item.loHang}</span>}
            {item.hanSuDung && <span>Hạn SD: {formatDate(item.hanSuDung)}</span>}
          </div>
        )}

        <div className="mt-2 flex items-center gap-1 flex-wrap">
          <button type="button" onClick={() => onView(item)} className="w-8 h-8 flex items-center justify-center rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors">
            <i className="ri-eye-line text-sm" />
          </button>
          <button type="button" onClick={() => onEdit(item)} className="w-8 h-8 flex items-center justify-center rounded-md text-slate-500 hover:text-agrida-700 hover:bg-agrida-50 transition-colors">
            <i className="ri-edit-line text-sm" />
          </button>
          <button type="button" onClick={() => onInStock(item)} className="w-8 h-8 flex items-center justify-center rounded-md text-slate-500 hover:text-sky-700 hover:bg-sky-50 transition-colors">
            <i className="ri-arrow-down-line text-sm" />
          </button>
          <button type="button" onClick={() => onOutStock(item)} disabled={!canXuat} className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors ${canXuat ? 'text-slate-500 hover:text-amber-700 hover:bg-amber-50' : 'text-slate-300 cursor-not-allowed'}`}>
            <i className="ri-arrow-up-line text-sm" />
          </button>
          <button type="button" onClick={() => onAdjust(item)} className="w-8 h-8 flex items-center justify-center rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors">
            <i className="ri-settings-3-line text-sm" />
          </button>
          <button type="button" onClick={() => onDelete(item)} className="w-8 h-8 flex items-center justify-center rounded-md text-slate-500 hover:text-red-700 hover:bg-red-50 transition-colors" title="Xóa">
            <i className="ri-delete-bin-line text-sm" />
          </button>
        </div>
      </div>
    </div>
  );
}